import { CardListSkeleton } from "components/new-stuff/ui/Skeleton"
import { useState } from "react"
import { Drawer, Progress } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import { useGet } from "utils/useFetch"
import { formatDate } from "utils/formateDate"
import { formatTimeReadble } from "utils/formatDate"
import { dptKRs } from "../../okrs/interface/department"
import { CustomSpin } from "components/custom/Loader"
// import * as XLSX from "xlsx"
// import { saveAs } from "file-saver"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
} from "chart.js"
import { Line } from "react-chartjs-2"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  ChartTooltip
)

interface ViewKRProps {
  open: boolean
  onClose: () => void
  data: dptKRs
  role: "admin" | "employee" | "manager"
  linkedObjectiveRolePath?: "admin" | "employee" | "manager" | "hod"
}

const METRIC_LABELS: Record<number, string> = {
  0: "Percentage",
  1: "Numeric",
  2: "Amount",
  3: "Binary",
}

const STATUS_INFO: Record<
  number,
  { label: string; color: string; bg: string }
> = {
  1: { label: "Not Started", color: "#0C1B3D", bg: "#EEF0F7" },
  2: { label: "At Risk", color: "#9A1D1F", bg: "#FCDCDC" },
  3: { label: "On Track", color: "#0C1B3D", bg: "#EEF0F7" },
  4: { label: "Achieved", color: "#1F703F", bg: "#C1EFC8" },
  5: { label: "Not Available", color: "#92660A", bg: "#FFF3D6" },
}

export const ViewKR = ({
  open,
  onClose,
  data,
  role,
  linkedObjectiveRolePath,
}: ViewKRProps) => {
  const [openActivity, setOpenActivity] = useState(false)

  const krId = data.employeeKeyResultId

  const { isLoading: isLoadingActivity, data: activityData } = useGet<
    IBaseRes<AuditEntity[]>
  >({
    url: `/api/v2/${role}/okr/employeekeyresultaudit/${data.employeeTargetDto?.[0]?.employeeId}/${krId}`,
    instanceKey: "newArchAxiosInstance",
    enabled: !!krId && open,
  })

  // Use departmentObjectiveTitle as a reliable signal for dept objective alignment
  // since the backend flags are only set when BOTH alignment types are present.
  const hasDeptAlignment =
    !!data.linkedToDepartmentObjective || !!data.departmentObjectiveTitle
  const hasCompanyAlignment = !!data.linkedToCompanyObjective

  const shouldLoadLinkedObjective =
    !!krId &&
    open &&
    (hasCompanyAlignment ||
      hasDeptAlignment ||
      !!linkedObjectiveRolePath ||
      role === "employee")

  const objectiveRolePath = linkedObjectiveRolePath || role

  const { isLoading: isLoadingLinkedObjective, data: linkedObjectiveData } =
    useGet<IKRLinkedObjectiveRes>({
      url: `/api/v2/${objectiveRolePath}/okr/okrframework/employeekeyresults/${krId}/objectives`,
      instanceKey: "newArchAxiosInstance",
      enabled: shouldLoadLinkedObjective,
      queryKey: [
        `/api/v2/${objectiveRolePath}/okr/okrframework/employeekeyresults`,
        String(krId),
        "objectives",
      ],
    })

  // All detail fields come exclusively from the data prop.
  const krTitle = data.keyResultTitle
  const metricLabel = METRIC_LABELS[data.metric] ?? "—"
  const isPercentage = data.metric === 0
  const startValue = data.employeeTargetDto?.[0]?.startValue ?? 0
  const endValue = data.employeeTargetDto?.[0]?.endValue ?? 0
  const progress = Number(
    (data.employeeTargetDto?.[0]?.currentProgress ?? 0).toFixed(2)
  )
  const ownerName = data.employeeTargetDto?.[0]?.employeeName ?? "—"
  const linkedObjective = linkedObjectiveData?.data
  // API returns the company-goal-linked objective; dept objective comes from data directly
  const companyGoalFromApi =
    linkedObjective?.objectiveType === 1 ? linkedObjective : null
  // Fall back to showing dept objective from API if objectiveType is 2 and we have no dept title
  const deptObjFromApi =
    linkedObjective?.objectiveType === 2 ? linkedObjective : null

  const sortedActivity = [...(activityData?.data ?? [])].sort(
    (a, b) =>
      new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
  )

  // Status comes from the latest audit entry when available.
  const latestAudit = sortedActivity[sortedActivity.length - 1]
  const status = latestAudit ? STATUS_INFO[latestAudit.status] ?? null : null

  // const handleExport = () => {
  //   // Sheet 1 — KR summary
  //   const summaryRows = [
  //     {
  //       "KR Title": krTitle,
  //       Department: data.departmentName,
  //       "Department Objective": data.departmentObjectiveTitle,
  //       "Measure As": metricLabel,
  //       "Weight (%)": data.weight ?? "Auto",
  //       Owner: ownerName,
  //       "Start Value": data.metric !== 3 ? startValue : "N/A",
  //       "End Value": data.metric !== 3 ? endValue : "N/A",
  //       "Current Progress (%)": progress,
  //       "Manager Progress (%)":
  //         data.employeeTargetDto?.[0]?.managerPercentageProgress ?? 0,
  //       "Due Date": data.employeeTargetDto?.[0]?.dueDate
  //         ? new Date(data.employeeTargetDto[0].dueDate).toLocaleDateString(
  //             "en-US",
  //             {
  //               month: "short",
  //               day: "numeric",
  //               year: "numeric",
  //             }
  //           )
  //         : "—",
  //       Status: status?.label ?? "—",
  //     },
  //   ]

  //   // Sheet 2 — activity log (chronological)
  //   const activityRows = sortedActivity.map((item) => ({
  //     Date: formatDate(item.createdDate),
  //     Time: formatTimeReadble(item.createdDate),
  //     Action: item.keyResultAction,
  //     "Progress (%)": item.currentProgress ?? 0,
  //     "Previous Progress (%)": item.previousProgress ?? 0,
  //     Status: STATUS_INFO[item.status]?.label ?? "—",
  //     "Changed By": item.createdByName,
  //   }))

  //   const wb = XLSX.utils.book_new()
  //   XLSX.utils.book_append_sheet(
  //     wb,
  //     XLSX.utils.json_to_sheet(summaryRows),
  //     "Summary"
  //   )
  //   XLSX.utils.book_append_sheet(
  //     wb,
  //     XLSX.utils.json_to_sheet(
  //       activityRows.length ? activityRows : [{ Note: "No activity yet" }]
  //     ),
  //     "Activity Log"
  //   )

  //   const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
  //   saveAs(
  //     new Blob([buffer], { type: "application/octet-stream" }),
  //     `kr_${data.employeeKeyResultId}_${data.keyResultTitle.replace(
  //       /\s+/g,
  //       "_"
  //     )}.xlsx`
  //   )
  // }

  const chartData = {
    labels: sortedActivity.map((item) =>
      new Date(item.createdDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    ),
    datasets: [
      {
        data: sortedActivity.map((item) => item.currentProgress ?? 0),
        borderColor: "#3B72EB",
        backgroundColor: "rgba(59, 114, 235, 0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: sortedActivity.length > 20 ? 0 : 4,
        pointHoverRadius: 4,
        pointBackgroundColor: "#3B72EB",
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value: string | number) => `${value}%`,
          font: { size: 11 },
          color: "#6B7280",
        },
        grid: { color: "rgba(0,0,0,0.06)", lineWidth: 1 },
        border: { dash: [4, 4] },
      },
      x: {
        ticks: {
          font: { size: 11 },
          color: "#6B7280",
          maxTicksLimit: 8,
          maxRotation: 0,
          autoSkip: true,
        },
        grid: { color: "rgba(0,0,0,0.06)", lineWidth: 1 },
        border: { dash: [4, 4] },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { parsed: { y: number } }) => `${ctx.parsed.y}%`,
        },
        titleFont: { size: 12 },
        bodyFont: { size: 13, weight: "bold" as const },
        bodyColor: "#3B72EB",
        backgroundColor: "#fff",
        titleColor: "#0C1B3D",
        borderColor: "#E5E7EB",
        borderWidth: 1,
        padding: 10,
      },
    },
  }

  return (
    <Drawer
      width={675}
      title={
        <span className="font-semibold text-padeBlack font-circular text-lg">
          Key Result Details
        </span>
      }
      open={open}
      onClose={onClose}
      closeIcon={false}
      className="bg-white relative"
      extra={
        <div className="flex items-center gap-3">
          {/* <button
            onClick={handleExport}
            className="flex items-center gap-1 text-primary500 text-sm font-semibold font-circular"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export
          </button> */}
          <button onClick={onClose}>
            <CloseOutlined className="h-6 w-6" />
          </button>
        </div>
      }
    >
      <CustomSpin
        placeholder={
          <CardListSkeleton
            items={3}
            lines={2}
          />
        }
        spinning={isLoadingActivity}
      >
        <div className="flex flex-col w-full font-circular">
          {/* KR Title */}
          <div className="flex items-center gap-3 mb-8">
            <span className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary50 text-primary500 font-semibold font-circular text-sm">
              KR
            </span>
            <h5 className="text-padeBlack font-semibold font-circular text-base">
              {krTitle}
            </h5>
          </div>

          {/* Details rows */}
          <div className="flex flex-col gap-5">
            {/* Progress */}
            <div className="flex justify-between items-center">
              <p className="text-padeSubtext font-circular text-sm font-medium">
                Progress
              </p>
              {data.metric === 3 ? (
                <p className="text-padeBlack text-sm font-semibold font-circular">
                  {progress === 100 ? "Yes" : "No"}
                </p>
              ) : data.metric === 1 || data.metric === 2 ? (
                <p className="text-padeBlack text-sm font-semibold font-circular">
                  {progress} / {endValue}
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-[177px]">
                    <Progress
                      percent={progress}
                      showInfo={false}
                      strokeColor={progress < 50 ? "#F41418" : "#13B378"}
                      trailColor="#EEF0F7"
                      className="!mb-0"
                    />
                  </div>
                  <p className="text-padeBlack text-sm font-semibold font-circular">
                    {progress}%
                  </p>
                </div>
              )}
            </div>

            {/* Status */}
            {status && (
              <div className="flex justify-between items-center">
                <p className="text-padeSubtext font-circular text-sm font-medium">
                  Status
                </p>
                <span
                  className="text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ color: status.color, backgroundColor: status.bg }}
                >
                  {status.label}
                </span>
              </div>
            )}

            {/* Weight */}
            <div className="flex justify-between items-center">
              <p className="text-padeSubtext font-circular text-sm font-medium">
                Weight
              </p>
              <p className="text-padeBlack text-sm font-semibold font-circular">
                {data.weight != null ? `${data.weight}%` : "Auto"}
              </p>
            </div>

            {/* Owner */}
            <div className="flex justify-between items-center">
              <p className="text-padeSubtext font-circular text-sm font-medium">
                Owner
              </p>
              <p className="text-padeBlack text-sm font-semibold font-circular">
                {ownerName}
              </p>
            </div>

            {/* Measure As */}
            <div className="flex justify-between items-center">
              <p className="text-padeSubtext font-circular text-sm font-medium">
                Measure As
              </p>
              <p className="text-padeBlack text-sm font-semibold font-circular">
                {metricLabel}
              </p>
            </div>

            {/* Start Value */}
            {data.metric !== 3 && (
              <div className="flex justify-between items-center">
                <p className="text-padeSubtext font-circular text-sm font-medium">
                  Start Value
                </p>
                <p className="text-padeBlack text-sm font-semibold font-circular">
                  {isPercentage ? `${startValue}%` : startValue}
                </p>
              </div>
            )}

            {/* End Value */}
            {data.metric !== 3 && (
              <div className="flex justify-between items-center">
                <p className="text-padeSubtext font-circular text-sm font-medium">
                  End Value
                </p>
                <p className="text-padeBlack text-sm font-semibold font-circular">
                  {isPercentage ? `${endValue}%` : endValue}
                </p>
              </div>
            )}

            {(hasDeptAlignment || hasCompanyAlignment) && (
              <>
                <div className="border-t border-[#D6D8DD] my-2" />

                {/* Department Objective alignment */}
                {hasDeptAlignment && (
                  <div className="flex flex-col gap-3">
                    <p className="text-padeBlack font-circular text-sm font-medium">
                      Department Objective
                    </p>
                    {isLoadingLinkedObjective &&
                    !data.departmentObjectiveTitle ? (
                      <div className="flex items-center gap-2">
                        <CustomSpin spinning />
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center gap-4">
                          <p className="text-padeSubtext font-circular text-sm font-medium">
                            Name
                          </p>
                          <p className="text-padeBlack text-sm font-semibold font-circular text-right">
                            {data.departmentObjectiveTitle ||
                              deptObjFromApi?.objectiveName ||
                              "—"}
                          </p>
                        </div>

                        {deptObjFromApi && (
                          <>
                            <div className="flex justify-between items-center">
                              <p className="text-padeSubtext font-circular text-sm font-medium">
                                Owner
                              </p>
                              <p className="text-padeBlack text-sm font-semibold font-circular">
                                {deptObjFromApi.owner || "—"}
                              </p>
                            </div>

                            <div className="flex justify-between items-center">
                              <p className="text-padeSubtext font-circular text-sm font-medium">
                                Review Cycle
                              </p>
                              <p className="text-padeBlack text-sm font-semibold font-circular">
                                {deptObjFromApi.reviewCycleName || "—"}
                              </p>
                            </div>

                            <div className="flex justify-between items-center">
                              <p className="text-padeSubtext font-circular text-sm font-medium">
                                Due Date
                              </p>
                              <p className="text-padeBlack text-sm font-semibold font-circular">
                                {deptObjFromApi.dueDate
                                  ? formatDate(deptObjFromApi.dueDate)
                                  : "—"}
                              </p>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Company Goal alignment */}
                {hasCompanyAlignment && (
                  <div className="flex flex-col gap-3 mt-3">
                    <p className="text-padeBlack font-circular text-sm font-medium">
                      Company Goal
                    </p>
                    {isLoadingLinkedObjective ? (
                      <div className="flex items-center gap-2">
                        <CustomSpin spinning />
                      </div>
                    ) : companyGoalFromApi ? (
                      <>
                        <div className="flex justify-between items-center gap-4">
                          <p className="text-padeSubtext font-circular text-sm font-medium">
                            Name
                          </p>
                          <p className="text-padeBlack text-sm font-semibold font-circular text-right">
                            {companyGoalFromApi.objectiveName || "—"}
                          </p>
                        </div>

                        <div className="flex justify-between items-center">
                          <p className="text-padeSubtext font-circular text-sm font-medium">
                            Review Cycle
                          </p>
                          <p className="text-padeBlack text-sm font-semibold font-circular">
                            {companyGoalFromApi.reviewCycleName || "—"}
                          </p>
                        </div>

                        <div className="flex justify-between items-center">
                          <p className="text-padeSubtext font-circular text-sm font-medium">
                            Due Date
                          </p>
                          <p className="text-padeBlack text-sm font-semibold font-circular">
                            {companyGoalFromApi.dueDate
                              ? formatDate(companyGoalFromApi.dueDate)
                              : "—"}
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-padeSubtext">
                        No company goal details available.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-[#D6D8DD] my-7" />

          {/* Progress Chart */}
          {sortedActivity.length > 0 && (
            <div className="mb-7">
              <p className="text-sm font-medium text-padeSubtext font-circular mb-3">
                Progress Value (%)
              </p>
              <div className="h-[300px]">
                <Line
                  data={chartData}
                  options={chartOptions}
                />
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-[#D6D8DD] my-7" />
          <div
            className="flex justify-between items-center mb-4 cursor-pointer"
            role="button"
            onClick={() => setOpenActivity(!openActivity)}
          >
            <h6 className="text-padeBlack font-semibold font-circular text-base">
              Activity
            </h6>
            <span>
              {openActivity ? (
                <ChevronUpIcon className="h-5 w-5 text-padeBlack" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-padeBlack" />
              )}
            </span>
          </div>

          {openActivity && (
            <div className="flex flex-col gap-4 mb-4">
              {isLoadingActivity ? (
                <div className="flex justify-center py-4">
                  <CustomSpin spinning />
                </div>
              ) : activityData?.data && activityData.data.length > 0 ? (
                activityData.data.map((item, idx) => (
                  <div key={idx}>
                    <span className="mb-0.5 text-sm font-medium font-circular text-padeBlack">
                      {item?.keyResultAction}
                    </span>
                    <p className="text-xs font-avenir text-padeSubtext">
                      {formatDate(item?.createdDate)}
                      {"   "}
                      {formatTimeReadble(item?.createdDate)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-padeSubtext">No activity yet.</p>
              )}
            </div>
          )}
        </div>
      </CustomSpin>
    </Drawer>
  )
}

type AuditRes = IBaseRes<AuditEntity[]>

export interface AuditEntity {
  keyResultAuditId: number
  departmentObjectiveId: number
  keyResultId: number
  employeeId: number
  keyResultAction: string
  currentProgress: number
  currentProgressPercentage: number
  previousProgress: number
  createdDate: string
  createdBy: string
  createdByName: string
  keyResult: string
  dueDate: string
  weight: number
  measureAs: number
  owner: string
  startValue: number
  endValue: number
  startDate: string
  endDate: string
  status: number
  statusDescription: string
}

interface IKRLinkedObjectiveRes {
  data: IKRLinkedObjective | null
  code: number
  message: string
  errors: Record<string, string[]>
  success: boolean
}

interface IKRLinkedObjective {
  objectiveId: number
  objectiveKey: string
  objectiveType: 1 | 2 | 3
  cycleDurationId: number
  cycleDurationKey: string
  objectiveName: string
  createdDate: string
  currentProgress: number
  reviewCycleName: string
  companyObjectiveStatus: number
  companyObjectiveStatusDescription: string
  owner: string
  startValue: number
  dueDate: string
}
